/**
 * LandingEditorTab — editor da landing pública com preview ao vivo.
 * Aba em Configurações. Puxa branding via /api/tenant/branding e salva por lá.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Save, Sparkles, Copy, ExternalLink, Eye, EyeOff, TrendingUp,
  Instagram, Facebook, MessageCircle, Image as ImageIcon, Star,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function LandingEditorTab() {
  const { slug } = useParams();
  const [branding, setBranding] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [stats, setStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const iframeRef = useRef(null);
  const iframeReadyRef = useRef(false);

  // Envia as alterações atuais pro iframe (live preview)
  const pushToPreview = useCallback((b) => {
    if (!iframeReadyRef.current || !iframeRef.current?.contentWindow || !b) return;
    iframeRef.current.contentWindow.postMessage(
      { type: "landing-preview-branding", branding: b },
      "*",
    );
  }, []);

  // Recebe "ready" do iframe e faz o primeiro push
  useEffect(() => {
    const onMsg = (evt) => {
      if (evt.data?.type === "landing-preview-ready") {
        iframeReadyRef.current = true;
        pushToPreview(branding);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [branding, pushToPreview]);

  // Sempre que branding muda, propaga (throttle simples via microtask)
  useEffect(() => {
    if (branding) pushToPreview(branding);
  }, [branding, pushToPreview]);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: st }] = await Promise.all([
        api.get("/tenant/settings"),
        api.get("/tenant/landing/stats").catch(() => ({ data: null })),
      ]);
      setBranding(s?.branding || {});
      setStats(st);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (k, v) => setBranding((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/branding", branding);
      toast.success("Landing salva!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const suggestHeadlines = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/tenant/landing/suggest-headlines");
      setSuggestions(data.headlines || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSuggesting(false); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/tenant/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patch("cover_url", data.url);
      toast.success("Capa enviada! Não esqueça de salvar.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publicUrl = `${window.location.origin}/empresa/${slug}`;
  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link copiado!");
  };

  if (!branding) return <div className="p-6"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  const isPublished = branding.landing_published !== false;

  return (
    <div className="space-y-6">
      {/* Header + publish + stats */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h3 className="font-display font-semibold text-lg">Landing pública</h3>
          <p className="text-sm text-muted-foreground">O link que você compartilha nas redes e no cartão.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="landing-publish-toggle">
            <Switch checked={isPublished} onCheckedChange={(v) => patch("landing_published", v)} />
            <span>{isPublished ? <><Eye className="w-3.5 h-3.5 inline mr-1" />Site publicado</> : <><EyeOff className="w-3.5 h-3.5 inline mr-1" />Rascunho</>}</span>
          </label>
        </div>
      </div>

      {/* Link público + stats */}
      <Card className="p-4 rounded-tenant border-border flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
          <code className="text-sm font-mono truncate" data-testid="landing-public-url">{publicUrl}</code>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copyLink} className="gap-1.5" data-testid="landing-copy-link">
            <Copy className="w-3 h-3" /> Copiar
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="landing-open-preview">
            <Button variant="outline" size="sm" className="gap-1.5">
              <ExternalLink className="w-3 h-3" /> Abrir
            </Button>
          </a>
        </div>
      </Card>

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <StatTile label="Visitas (30d)" value={stats.views_30d} testid="stat-views" />
          <StatTile label="Agendamentos" value={stats.conversions_30d} testid="stat-conv" />
          <StatTile label="Conversão" value={`${stats.conversion_rate}%`} testid="stat-rate" icon={<TrendingUp className="w-3 h-3" />} />
        </div>
      )}

      {/* Split: editor + preview */}
      <div className="grid lg:grid-cols-[1fr_400px] gap-6">
        <div className="space-y-4">
          {/* Cover upload */}
          <Card className="p-4 space-y-3 rounded-tenant border-border">
            <Label className="text-sm font-semibold">Foto de capa (banner)</Label>
            <p className="text-xs text-muted-foreground">Recomendado 1920×720px. Aparece no topo do site.</p>
            {branding.cover_url && (
              <div className="relative h-32 rounded-md overflow-hidden border border-border" data-testid="landing-cover-preview">
                <img src={branding.cover_url} alt="Capa" className="w-full h-full object-cover" />
                <button onClick={() => patch("cover_url", "")}
                  className="absolute top-2 right-2 text-xs bg-red-500 text-white px-2 py-1 rounded"
                  data-testid="landing-cover-remove">Remover</button>
              </div>
            )}
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer border border-border rounded-md px-3 py-2 hover:bg-muted">
              <ImageIcon className="w-4 h-4" />
              {branding.cover_url ? "Trocar capa" : "Enviar capa"}
              <input type="file" accept="image/*" className="hidden" data-testid="landing-cover-input"
                onChange={(e) => uploadCover(e.target.files?.[0])} />
            </label>
          </Card>

          {/* Headline */}
          <Card className="p-4 space-y-3 rounded-tenant border-border">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Frase de destaque (headline)</Label>
              <Button variant="ghost" size="sm" onClick={suggestHeadlines} disabled={suggesting} className="gap-1.5 h-7 text-xs" data-testid="landing-suggest-btn">
                {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Sugerir com IA
              </Button>
            </div>
            <Textarea rows={2} maxLength={120} data-testid="landing-headline"
              value={branding.headline || ""} onChange={(e) => patch("headline", e.target.value)}
              placeholder='Ex.: "Barbearia clássica desde 2015"' />
            <div className="text-[10px] text-muted-foreground text-right">{(branding.headline || "").length}/120</div>
            {suggestions.length > 0 && (
              <div className="space-y-1.5 pt-2 border-t border-border" data-testid="landing-suggestions">
                <div className="text-xs text-muted-foreground">Clique para usar:</div>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => { patch("headline", s); setSuggestions([]); }}
                    data-testid={`landing-suggestion-${i}`}
                    className="w-full text-left text-sm p-2 rounded border border-border hover:bg-muted">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Sobre */}
          <Card className="p-4 space-y-3 rounded-tenant border-border">
            <Label className="text-sm font-semibold">Sobre a empresa (opcional)</Label>
            <p className="text-xs text-muted-foreground">Parágrafo curto que aparece abaixo dos destaques.</p>
            <Textarea rows={4} maxLength={500} data-testid="landing-about"
              value={branding.about || ""} onChange={(e) => patch("about", e.target.value)} />
          </Card>

          {/* Modo de exibição */}
          <Card className="p-4 space-y-3 rounded-tenant border-border">
            <Label className="text-sm font-semibold">Modo de exibição da Landing Page</Label>
            <p className="text-xs text-muted-foreground">Controle o que aparece na sua página pública para os clientes.</p>
            <div className="grid gap-2">
              {[
                { key: "hybrid", title: "Híbrido (padrão)", desc: "Mostra vitrine de produtos e agenda de serviços." },
                { key: "booking_only", title: "Apenas Agendamento", desc: "Oculta a vitrine e foca 100% no calendário de serviços." },
                { key: "store_only", title: "Apenas Vitrine", desc: "Oculta o agendamento e transforma a página em catálogo de produtos com carrinho." },
              ].map((opt) => {
                const active = (branding.landing_display_mode || "hybrid") === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => patch("landing_display_mode", opt.key)}
                    className={`text-left p-3 rounded-lg border-2 transition ${active ? "border-tenant bg-tenant/5" : "border-border hover:border-tenant/40"}`}
                    style={{ borderColor: active ? "hsl(var(--tenant-primary))" : undefined }}
                    data-testid={`landing-mode-${opt.key}`}
                  >
                    <div className="font-medium text-sm">{opt.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Redes sociais */}
          <Card className="p-4 space-y-3 rounded-tenant border-border">
            <Label className="text-sm font-semibold">Redes sociais</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              <SocialField icon={<Instagram className="w-3.5 h-3.5" />} label="Instagram" placeholder="tombarber" value={branding.instagram || ""}
                onChange={(v) => patch("instagram", v.replace("@", ""))} testid="landing-instagram" />
              <SocialField icon={<Facebook className="w-3.5 h-3.5" />} label="Facebook" placeholder="tombarber" value={branding.facebook || ""}
                onChange={(v) => patch("facebook", v)} testid="landing-facebook" />
              <SocialField icon={<span className="text-xs">🎵</span>} label="TikTok" placeholder="tombarber" value={branding.tiktok || ""}
                onChange={(v) => patch("tiktok", v.replace("@", ""))} testid="landing-tiktok" />
              <SocialField icon={<MessageCircle className="w-3.5 h-3.5" />} label="WhatsApp Business" placeholder="5511987654321" value={branding.whatsapp_business || ""}
                onChange={(v) => patch("whatsapp_business", v.replace(/\D/g, ""))} testid="landing-whatsapp" />
              <SocialField icon={<Star className="w-3.5 h-3.5" />} label="Avaliação no Google (URL)" placeholder="https://g.page/r/CX.../review" value={branding.google_review_url || ""}
                onChange={(v) => patch("google_review_url", v.trim())} testid="landing-google-review" />
            </div>
            <p className="text-[11px] text-muted-foreground pt-1">
              💡 <strong>Como pegar sua URL do Google:</strong> abra sua ficha no Google Maps → botão &quot;Compartilhar&quot; → aba &quot;Incorporar um mapa&quot; ou use&nbsp;
              <a href="https://www.google.com/business/manage/" target="_blank" rel="noreferrer" className="underline">Meu Negócio no Google → Início → Peça avaliações</a>.
            </p>
          </Card>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="landing-save"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar alterações
            </Button>
          </div>
        </div>

        {/* Preview mobile — live via postMessage */}
        <div className="lg:sticky lg:top-6 space-y-2 self-start">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground text-center flex items-center justify-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Preview ao vivo (mobile)
          </div>
          <div className="mx-auto max-w-[360px] rounded-[2rem] border-8 border-foreground/90 shadow-2xl overflow-hidden bg-background">
            <div className="h-[600px] overflow-hidden relative">
              <iframe
                ref={iframeRef}
                src={`/empresa/${slug}?_preview=1`}
                title="Preview da landing"
                data-testid="landing-preview-iframe"
                className="w-[360px] h-[600px] border-0 origin-top-left"
              />
            </div>
          </div>
          <p className="text-[10px] text-center text-muted-foreground">Suas alterações aparecem instantaneamente. Salve para publicar.</p>
        </div>
      </div>
    </div>
  );
}

function SocialField({ icon, label, value, onChange, placeholder, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs flex items-center gap-1.5">{icon} {label}</Label>
      <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function StatTile({ label, value, icon, testid }) {
  return (
    <div className="p-3 rounded-md border border-border bg-card" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="font-display text-2xl font-bold mt-0.5">{value ?? 0}</div>
    </div>
  );
}
